import { StepConfig } from "../_types/wizard-types";
export { EVENT_SOURCE_OPTIONS, EVENT_SOURCE_OTHER_VALUE } from "@/lib/events/event-source-options";

/** Maximum character length for event names. */
export const NAME_MAX_LENGTH = 64;

/**
 * Query-parameter key that signals a wizard resume after the auth roundtrip.
 * Kept here so the writer (VenuePreviewResults submit handler) and the reader
 * (CreateEventWizard mount) can't drift.
 */
export const RESUME_QUERY_PARAM = "resume";

/** Expected value of `RESUME_QUERY_PARAM` when resuming the wizard. */
export const RESUME_QUERY_VALUE = "1";

/** Path the wizard returns to with resume signaling appended. */
export const WIZARD_RESUME_PATH = `/create?${RESUME_QUERY_PARAM}=${RESUME_QUERY_VALUE}`;

/** Number of venue results to show in the final results view. */
export const RESULTS_LIMIT = 5;

/** Debounce window before a draft field change autosaves. Matches `event-details`'
 *  `AUTO_SAVE_DELAY_MS` — long enough to coalesce typing, short enough to feel responsive. */
export const AUTOSAVE_DELAY_MS = 1500;

/** Minimum time (ms) each of `SearchProgress`'s first two steps stays visible before advancing —
 *  pure pacing so the sequence doesn't flash by unreadably fast, not tied to the real search. */
export const SEARCH_PROGRESS_MIN_STEP_DURATION = 500;
/** Minimum time (ms) `SearchProgress`'s final step stays visible after the real search resolves,
 *  before calling `onComplete` — gated on the fetch instead of a fixed wall-clock wait. */
export const SEARCH_PROGRESS_FINAL_STEP_MIN_DURATION = 400;

/** Below this venue budget, non-admin users see the "not the right fit" warning modal on blur. */
export const LOW_BUDGET_THRESHOLD = 20_000;

/** Max locations selectable on the location step — the autocomplete and suggestion pills share this. */
export const LOCATION_LIMIT = 5;

/** Minimum hotel rooms per night required to source through the platform. */
export const MIN_ROOMS_PER_NIGHT = 10;

/**
 * Whether a chosen max-rooms-per-night is below the platform minimum and should
 * block progression past the guestrooms step.
 * @param count - Max rooms per night the user entered.
 * @returns `true` if `count` is below {@link MIN_ROOMS_PER_NIGHT}.
 */
export function isBelowRoomMinimum(count: number): boolean {
  return count < MIN_ROOMS_PER_NIGHT;
}

/** Per-IP rate-limit window for the public venue-preview search server action. */
export const PREVIEW_SEARCH_RATE_LIMIT_WINDOW_MS = 60_000;

/** Per-IP max calls per window for the public venue-preview search server action (proxies a privileged,
 *  billed internal API with no auth gate). */
export const PREVIEW_SEARCH_RATE_LIMIT_MAX = 30;

/** Max payloads (locations) accepted per `searchVenuePreviews` call — matches {@link MAX_PREFILL_CITIES},
 *  the location step's own selection limit. Without this, one call could batch far more than a legitimate
 *  search ever needs, fanning out to unlimited billed upstream fetches while the rate limiter (checked
 *  once per call, not per payload) only sees a single hit. */
export const MAX_PREVIEW_SEARCH_PAYLOADS = 5;

/** Per-IP rate-limit window for the public Northstar-request lookup server action. */
export const NORTHSTAR_REQUEST_RATE_LIMIT_WINDOW_MS = 60_000;

/** Per-IP max calls per window for the public Northstar-request lookup (unauthenticated, service-role
 *  read keyed only by an unguessable uuid — bounds it from becoming an unrate-limited enumeration oracle
 *  if a link leaks). */
export const NORTHSTAR_REQUEST_RATE_LIMIT_MAX = 30;

/** A clickable suggested-location pill: city-only `label` for display, `query` to disambiguate the
 *  Google Places lookup. */
export interface SuggestedLocation {
  label: string;
  query: string;
}

/**
 * Curated top event cities shown as one-click suggestion pills on the location step. Derived from a
 * one-off aggregation of `nowadays.events.formatted_locations` (top cities by event count); kept static
 * to avoid a runtime query. Refresh by re-running that aggregation. `query` includes the state so the
 * region autocomplete resolves the intended city.
 */
export const SUGGESTED_LOCATIONS: SuggestedLocation[] = [
  { label: "San Francisco", query: "San Francisco, CA" },
  { label: "New York", query: "New York, NY" },
  { label: "Chicago", query: "Chicago, IL" },
  { label: "Atlanta", query: "Atlanta, GA" },
  { label: "Austin", query: "Austin, TX" },
];

/** Sentinel `place_id` prefix for an optimistic (not-yet-resolved) location added from a suggestion pill.
 *  Real Google place ids never collide with this, so it uniquely identifies the placeholder to reconcile. */
export const OPTIMISTIC_PLACE_ID_PREFIX = "optimistic:";

/**
 * Query params for a Northstar partner redirect into `/create`. Shared between the writer
 * (`/create-northstar-event` resolver) and the reader (`CreateEventWizard` mount) so they can't drift.
 */
export const NORTHSTAR_QUERY_PARAM = "northstar";

/** Expected value of `NORTHSTAR_QUERY_PARAM` for an active Northstar redirect. */
export const NORTHSTAR_QUERY_VALUE = "1";

/** Query param carrying the opaque `draft_events.uuid`. */
export const NORTHSTAR_ID_PARAM = "ns_id";

/**
 * Query param signaling a deliberate "start fresh" entry into `/create` (e.g. the "New event" option
 * in `CreateEventModal`, as opposed to a bare `/create` visit). When present, `page.tsx` skips fetching
 * the user's most recent open draft, so a half-finished draft from a previous session never silently
 * resumes into a flow the user explicitly asked to start empty. The old draft itself is left alone —
 * this only affects whether THIS visit resumes it, not whether it still exists.
 */
export const CREATE_EVENT_FRESH_START_PARAM = "fresh";

/**
 * Query param carrying `enterprise_organizations.id` when a Nowadays admin launches `/create` from
 * admin-dash's org "Create event" button (ENG-918), to create the event for that org rather than the
 * admin's own account. Read and re-authorized server-side in `resolveAdminOrgLaunchContext` — never
 * trusted client-side, since anyone can append this param to their own `/create` visit.
 */
export const ADMIN_ORG_ID_PARAM = "admin_org_id";

/** Defensive cap on how many requested venues a single Northstar request hydrates/persists. */
export const MAX_INJECTED_VENUES = 12;

/** Max distinct cities prefilled into the location step from a Northstar request's requested venues
 *  (matches the location step's selection limit). */
export const MAX_PREFILL_CITIES = 5;

/** All Northstar-requested venues are persisted as top-ranked candidates (mirrors the legacy flow). */
export const NORTHSTAR_CANDIDATE_RANK = 1;

/**
 * Ordered step configuration for the wizard. Dates sits right after Location (inside
 * "about-your-event") so headcount, guestrooms, and meeting rooms can all derive their dates from
 * the preferred event date rather than asking again — see `HeadcountStep`, `GuestroomsStep`, and
 * `MeetingRoomsStep`. "final-details" is terminal: completing it sets `isComplete` and the wizard
 * goes straight to venue-preview submission, exactly like v2's own terminal step ("flexibility") did.
 */
export const WIZARD_STEPS: StepConfig[] = [
  { id: "about-your-event", label: "About your event" },
  { id: "dates", label: "Dates" },
  { id: "headcount", label: "Headcount" },
  { id: "guestrooms", label: "Guestrooms" },
  { id: "meeting-rooms", label: "Meeting rooms" },
  { id: "food-beverage", label: "Food & beverage" },
  { id: "agenda", label: "Agenda" },
  { id: "budget-decision", label: "Budget & decision" },
  { id: "final-details", label: "Final details" },
];

/** Animation variants for wizard-level step transitions. */
export const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -300 : 300,
    opacity: 0,
  }),
};

/** Transition config for wizard-level step animations. */
export const stepTransition = {
  type: "tween" as const,
  duration: 0.35,
  ease: "easeInOut" as const,
};

/** Animation variants for sub-phase transitions within a step. */
export const subPhaseVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 150 : -150,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -150 : 150,
    opacity: 0,
  }),
};

/** Staggered entrance variants for individual form elements. */
export const formElementVariants = {
  hidden: { opacity: 0 },
  visible: (i: number) => ({
    opacity: 1,
    transition: { delay: i * 0.08, duration: 0.35, ease: "easeOut" as const },
  }),
};
